// Message-window sizes are runtime-configurable via env vars:
//   T3CODE_INITIAL_MESSAGE_WINDOW   — initial thread-subscription snapshot size (default 50)
//   T3CODE_OLDER_MESSAGE_PAGE_SIZE  — older messages per reverse-scroll page (default 25)
//
// Read at module load: real `process.env` on the server (so a value set when
// launching `t3 serve` takes effect at runtime), and on the web the
// `process.env.T3CODE_*` tokens are inlined by the Vite `define` build step
// (see apps/web/vite.config.ts). `process` is declared ambiently so this
// browser-safe package needn't depend on @types/node — keep the member
// expressions verbatim so the Vite define matches.
declare const process: { readonly env: Record<string, string | undefined> };

/** A snapshot window of this size carries the whole thread and reverse pagination stays off. */
export const WHOLE_THREAD_WINDOW = 0;

/** Default initial / page size when the corresponding env var is unset or invalid. */
export const DEFAULT_INITIAL_MESSAGE_WINDOW = 50;
export const DEFAULT_OLDER_MESSAGE_PAGE_SIZE = 25;

/**
 * Parse a message-window size from an env string. Falls back to `fallback`
 * unless the value parses to a positive integer (rejects empty/unset, `0`,
 * negatives, decimals, and non-numeric input — all of which would be unusable
 * as a SQL `LIMIT` / window size).
 */
export const parseMessageWindowSize = (raw: string | undefined, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : fallback;
};

/** Parse an initial snapshot window: a positive integer, or `0` for the whole thread. */
export const parseInitialWindowSize = (raw: string | undefined, fallback: number): number => {
  if (raw === undefined || raw.trim() === "") {
    return fallback;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

/** Number of most-recent messages included in a thread subscription snapshot; `0` = whole thread. */
export const INITIAL_MESSAGE_WINDOW = parseInitialWindowSize(
  process.env.T3CODE_INITIAL_MESSAGE_WINDOW,
  DEFAULT_INITIAL_MESSAGE_WINDOW,
);
/** Number of older messages fetched per reverse-scroll page. */
export const OLDER_MESSAGE_PAGE_SIZE = parseMessageWindowSize(
  process.env.T3CODE_OLDER_MESSAGE_PAGE_SIZE,
  DEFAULT_OLDER_MESSAGE_PAGE_SIZE,
);
